import Phaser from "phaser";
import { COMPILED_COLLISION_CELLS_PER_TILE, type CompiledChunk, type StandLookup, type WorldManifest } from "daedalus-core";
import type { SolidMasks } from "daedalus-renderer-phaser";
import { BAKED_SHEET, CHARACTER_MANIFEST, SOLID, type Appearance, type CollisionGrid, type CreatureLook, type GameObjectTypes, type NpcLook } from "odyssey-shared";
import { loadCreatureLooks, loadGameObjectTypes, loadNpcLooks } from "../editor/version-tables.ts";
import type { LookFigures } from "./looks/figures.ts";
import { fetchNamed, loadClientChunk, loadWorld, loadWorldVersion, type LoadedWorld, type WorldFile } from "../editor/world-loader.ts";
import { withOwnLook, type OwnLook, type OwnLookSource } from "./character-layers.ts";
import { characterStrips, parseCharacterManifest, type CharacterManifest } from "./character.ts";
import { chunkKey } from "./chunk-key.ts";
import type { LoadingEvent } from "./loading-state.ts";
import { AtlasPageHolder, loadFiles } from "./atlas-holder.ts";
import { assembleStand } from "./ground-stand.ts";
import { assembleSurfaces, type GroundSurfaces } from "./ground-surfaces.ts";
import { gameReport } from "./report.ts";
import { ProjectileLooks } from "./world/projectile-looks.ts";
import { WindSheets } from "./weather/wind-sheets.ts";

/**
 * Everything the game scene loads before it joins (P0.4-T4, P1.4-T5), in the order it waits on it: the
 * world version's pointer; its manifests (the world manifest, the asset manifest, the version's contract with its
 * creature look table beside it, ADR-082 decision 5, and the character manifest); the atlas pages and the character's strips, in one batch of Phaser's loader;
 * and every chunk of the map, whose collision the scene assembles into the grid it predicts against. The projectile
 * looks' pointer and strips load beside the art (ADR-095 decision 19, `world/projectile-looks.ts`); the boot waits for
 * them, so a page that joins mid-flight draws the rest of it, and a look that does not load is no failure of the boot. Each
 * step tells the loading shell when it starts and how many of its items it holds as each lands, so the
 * shell's bar shows only finished work.
 *
 * A run that fails throws with a message naming what failed (a URL and its status, or a file that could
 * not be read), and a later run resumes where it stopped: whatever arrived stays, a failed chunk or page is
 * asked for again, and nothing is fetched twice. Phaser's loader reports a failed file without failing its
 * batch (a missing page would throw only later, and a missing strip would draw Phaser's missing texture), so
 * the boot fails the batch itself.
 */

/**
 * The manifests step's files: the world manifest, the asset manifest, the contract and the character
 * manifest.
 */
const MANIFEST_FILES = 4;

/**
 * The progress a quiet boot reports: none, for a map a crossing boots behind the world shown (ADR-147 decision 18).
 */
const QUIET = (): void => undefined;

/**
 * How many chunk requests the work ahead of a crossing runs at once: a neighbour is fetched behind the world shown, so
 * it never competes with the map stood on in a burst.
 */
export const PREFETCH_CONCURRENCY = 4;

/**
 * The sheets of the character manifest the game never draws from: the fallback look baked for the editor's stand-in
 * (ADR-094 decision 33), since the game composes every player from the look sheets.
 */
const EDITOR_SHEETS: ReadonlySet<string> = new Set([BAKED_SHEET]);

/**
 * One map of a world manifest.
 */
type WorldMap = WorldManifest["maps"][number];

/**
 * What the scene joins with: the world, its object types, its creature and NPC looks, the character manifest, the atlas's solid pixel
 * masks (null when they could not be read, which only stops props fading over the character), the map, its
 * chunks by `cx,cy`, the collision grid with a copy of its cells as compiled, and what each tile of the map is made
 * of, for footsteps (ADR-085's amendment for P1.9-T84). `stand` is the map's Behind and In front marks (ADR-132), null when no chunk carries any. `ownLook` is the look the page's own character wears in place
 * of the default body, or null for the default. `projectiles` holds the projectile looks the page loaded, none when
 * their pointer did not answer.
 */
export interface BootedWorld {
  readonly world: LoadedWorld;
  readonly types: GameObjectTypes;
  readonly looks: readonly CreatureLook[];
  readonly npcLooks: readonly NpcLook[];
  readonly characters: CharacterManifest;
  readonly ownLook: OwnLook | null;
  readonly projectiles: ProjectileLooks;
  readonly windSheets?: WindSheets;
  readonly masks: SolidMasks | null;
  readonly map: WorldMap;
  readonly chunks: ReadonlyMap<string, CompiledChunk>;
  readonly grid: CollisionGrid;
  readonly baseCells: Uint8Array;
  readonly surfaces: GroundSurfaces;
  readonly stand: StandLookup | null;
}

/**
 * Loads a look for the page's own character: its sheets by id, added to the character manifest so their strips load
 * with the rest, and the look.
 */
export type OwnLookLoader = OwnLookSource;

/**
 * Where the boot loads from and whom it tells. `running` names the version the page's map server runs, asked once
 * before the world loads (ADR-050's amendment of 2026-10-02); when it names none, or that version does not load, the
 * pointer is read as before. `ownLook`, given only on a local page asked for a trial look (P1.9-T88, `trial-gate.ts`),
 * loads the sheets of a look for the page's own character from the asset base; when it fails the boot warns and the
 * character wears the default body. `looks` is the page's composed looks (ADR-094 decision 16): the boot loads their
 * pointer with the manifests and the fallback look's piece sheets with the character's strips, so the loading shell
 * shows both, and the sheets of the page's own look when `ownAppearance` knows it (ADR-094 note 87).
 */
export interface WorldBootOptions {
  readonly base: string;
  readonly worldId: string;
  readonly pointer: "current" | "published";
  readonly mapId: string;
  readonly progress: (event: LoadingEvent) => void;
  readonly running?: () => Promise<{ readonly version: string; readonly source: "status" | "welcome" } | null>;
  readonly ownLook?: OwnLookLoader;
  readonly looks?: LookFigures;
  readonly ownAppearance?: () => Appearance | null;
  /** Fetches one client chunk; `loadClientChunk` when left out, a test's stand-in otherwise. */
  readonly loadChunk?: (world: LoadedWorld, mapId: string, cx: number, cy: number) => Promise<CompiledChunk>;
}

/**
 * The world boot of one scene.
 */
export class WorldBoot {
  private loaded: LoadedWorld | null = null;
  private types: GameObjectTypes | null = null;
  private looks: readonly CreatureLook[] = [];
  private npcLooks: readonly NpcLook[] = [];
  private characters: CharacterManifest | null = null;
  private ownLook: OwnLook | null = null;
  private readonly projectiles = new ProjectileLooks();
  private readonly windSheets = new WindSheets();
  private art = false;
  private chunks = new Map<string, CompiledChunk>();
  private readonly chunkSets = new Map<string, Map<string, CompiledChunk>>();
  private booted: BootedWorld | null = null;

  /**
   * The atlas pages the scene holds: which are loaded and which the last atlas steps counted.
   */
  public readonly atlas: AtlasPageHolder;

  /**
   * @param scene The scene whose loader and textures take the art.
   * @param options Where to load from and whom to tell.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly options: WorldBootOptions,
  ) {
    this.atlas = new AtlasPageHolder(scene);
  }

  /**
   * Loads what is missing and assembles the world, or hands back the world a run already assembled.
   *
   * @returns The booted world.
   * @throws Error naming what failed: a file that did not arrive (its URL and status), one that could not be
   *   read (a chunk, a contract or an asset manifest of another schema by its own error class), a map the world
   *   lacks, or a chunk without a collision grid.
   */
  public async run(): Promise<BootedWorld> {
    return this.runMap(this.options.mapId);
  }

  /**
   * Loads what is missing and assembles one map of the world, or hands back the map a run already assembled. The world's
   * art, manifests and looks load once; a map other than the one held is fetched whole (a room is at most 64 chunks) and
   * replaces it, so the chunks of the map left are not held (ADR-122 decision 2.9).
   *
   * @param mapId The map to assemble.
   * @param quiet True for a map a crossing boots behind the world shown, which reports no progress to the loading shell.
   * @returns The booted world.
   * @throws Error naming what failed, as `run` does.
   */
  public async runMap(mapId: string, quiet = false): Promise<BootedWorld> {
    if (this.booted !== null && this.booted.map.id === mapId) {
      return this.booted;
    }
    const progress = quiet ? QUIET : this.options.progress;
    const world = await this.version();
    const types = await this.objectTypes(world);
    const characters = await this.characterManifest();
    const projectiles = this.projectiles.start(this.scene.textures, this.options.base);
    const windSheets = this.windSheets.start(this.scene.textures, this.options.base);
    await this.artwork(world, characters, mapId, progress);
    await projectiles;
    await windSheets;
    const map = world.manifest.maps.find((candidate) => candidate.id === mapId);
    if (map === undefined) {
      throw new Error(`world has no map ${mapId}`);
    }
    this.holdChunksOf(map.id);
    await this.fetchChunks(world, map, this.chunks, progress);
    this.booted = { world, types, looks: this.looks, npcLooks: this.npcLooks, characters, ownLook: this.ownLook, projectiles: this.projectiles, windSheets: this.windSheets, masks: this.atlas.masks, map, chunks: this.chunks, ...this.assemble(world, map, this.chunks), surfaces: assembleSurfaces((cx, cy) => this.chunks.get(chunkKey(cx, cy))?.surfaces, map.width, map.height, world.manifest.chunkSize), stand: assembleStand((cx, cy) => this.chunks.get(chunkKey(cx, cy))?.stand, map.width, map.height, world.manifest.chunkSize) };
    this.atlas.stand(map.id);
    return this.booted;
  }

  /**
   * Makes a map's chunks the ones the boot holds and fetches into. The set of the map just left is kept, so a way back
   * through a door asks for nothing it already holds; the sets of every map before it are dropped.
   *
   * @param mapId The map the boot assembles next.
   */
  private holdChunksOf(mapId: string): void {
    const held = this.chunkSets.get(mapId);
    if (held === this.chunks) {
      return;
    }
    const previous = this.atlas.standing;
    for (const id of [...this.chunkSets.keys()]) {
      if (id !== mapId && id !== previous) {
        this.chunkSets.delete(id);
      }
    }
    this.chunks = held ?? new Map<string, CompiledChunk>();
    this.chunkSets.set(mapId, this.chunks);
    this.booted = null;
  }

  /**
   * Takes chunks of a map the page already holds, as the ground beside the map stood on does for a neighbour a crossing
   * reaches (ADR-147 decision 18), so `runMap` fetches only the rest.
   *
   * @param mapId The map's id.
   * @param chunks Its chunks, keyed by `chunkKey`.
   */
  public adopt(mapId: string, chunks: ReadonlyMap<string, CompiledChunk>): void {
    let held = this.chunkSets.get(mapId);
    if (held === undefined) {
      held = new Map<string, CompiledChunk>();
      this.chunkSets.set(mapId, held);
    }
    for (const [key, chunk] of chunks) {
      if (!held.has(key)) {
        held.set(key, chunk);
      }
    }
  }

  /**
   * The chunks the boot holds of a map, which a crossing hands the ground beside the map reached for the map left.
   *
   * @param mapId The map's id.
   * @returns The chunks, or null when it holds none of that map.
   */
  public chunksOf(mapId: string): ReadonlyMap<string, CompiledChunk> | null {
    return this.chunkSets.get(mapId) ?? null;
  }

  /**
   * Fetches every chunk of a neighbour the page may cross to, with no loading screen and nothing else changed (ADR-147
   * decision 18), so the crossing's `runMap` finds them held. The chunks join any the boot already holds of that map. At
   * most `PREFETCH_CONCURRENCY` requests run at once, one R2 read a chunk, each chunk asked for once while held; a
   * neighbour let go (`forget`) stops the fetch at its next chunk.
   *
   * @param mapId The neighbour's id.
   * @returns Its chunks, keyed by `chunkKey`, once every one is in; null when it was let go meanwhile.
   * @throws Error naming the first chunk that failed, or a map the world does not have.
   */
  public async prefetch(mapId: string): Promise<ReadonlyMap<string, CompiledChunk> | null> {
    const world = await this.version();
    const map = world.manifest.maps.find((candidate) => candidate.id === mapId);
    if (map === undefined) {
      throw new Error(`world has no map ${mapId}`);
    }
    let held = this.chunkSets.get(mapId);
    if (held === undefined) {
      held = new Map<string, CompiledChunk>();
      this.chunkSets.set(mapId, held);
    }
    const into = held;
    const columns = Math.ceil(map.width / world.manifest.chunkSize);
    const total = columns * Math.ceil(map.height / world.manifest.chunkSize);
    let next = 0;
    let failure: unknown = null;
    const worker = async (): Promise<void> => {
      while (next < total && failure === null && this.chunkSets.get(mapId) === into) {
        const index = next;
        next += 1;
        const cx = index % columns;
        const cy = Math.floor(index / columns);
        if (into.has(chunkKey(cx, cy))) {
          continue;
        }
        try {
          const chunk = await this.chunkOf(world, mapId, cx, cy);
          if (this.chunkSets.get(mapId) === into) {
            into.set(chunkKey(cx, cy), chunk);
          }
        } catch (error) {
          failure = error;
        }
      }
    };
    await Promise.all(Array.from({ length: PREFETCH_CONCURRENCY }, worker));
    if (failure !== null) {
      throw failure;
    }
    return this.chunkSets.get(mapId) === into ? into : null;
  }

  /**
   * Lets go of the chunks fetched ahead for a neighbour the ground beside the map no longer draws (ADR-147 decision 9), so
   * a seam approached and left holds nothing; a fetch still running for it stops at its next chunk. The map stood on and
   * the map a crossing booted are kept.
   *
   * @param mapId The neighbour's id.
   */
  public forget(mapId: string): void {
    if (this.chunks !== this.chunkSets.get(mapId) && this.booted?.map.id !== mapId) {
      this.chunkSets.delete(mapId);
    }
  }

  /**
   * Fetches one client chunk.
   *
   * @param world The loaded world.
   * @param mapId The map's id.
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @returns The chunk.
   */
  private chunkOf(world: LoadedWorld, mapId: string, cx: number, cy: number): Promise<CompiledChunk> {
    return (this.options.loadChunk ?? loadClientChunk)(world, mapId, cx, cy);
  }

  /**
   * What a neighbour's ground is made of, from the chunks the boot holds of it, for work a crossing does ahead (its
   * puddles): the map, its chunks, its collision cells as compiled and its surfaces.
   *
   * @param mapId The neighbour's id.
   * @returns The ground, or null before the world loads or when the boot holds none of the map's chunks.
   */
  public groundOf(mapId: string): { readonly map: WorldMap; readonly chunks: ReadonlyMap<string, CompiledChunk>; readonly baseCells: Uint8Array; readonly surfaces: GroundSurfaces } | null {
    const world = this.loaded;
    const map = world?.manifest.maps.find((candidate) => candidate.id === mapId);
    const chunks = this.chunkSets.get(mapId);
    if (world === null || map === undefined || chunks === undefined || chunks.size === 0) {
      return null;
    }
    return { map, chunks, baseCells: this.assemble(world, map, chunks).baseCells, surfaces: assembleSurfaces((cx, cy) => chunks.get(chunkKey(cx, cy))?.surfaces, map.width, map.height, world.manifest.chunkSize) };
  }

  /**
   * The loaded world, or null before it loads.
   *
   * @returns The world.
   */
  public get world(): LoadedWorld | null {
    return this.loaded;
  }

  /**
   * The world version the boot loaded, for a failure that names it.
   *
   * @returns The version, or null before its manifests have loaded.
   */
  public loadedVersion(): string | null {
    return this.loaded?.version ?? null;
  }

  /**
   * The world version the map server runs (`running`), or else the one the pointer names, with its manifest and asset
   * manifest: the world step, and the first two of the manifests step's files. A running version that does not load
   * (another world's, or one the CDN lacks) falls back to the pointer, whose version the `welcome` then checks.
   *
   * @returns The loaded world.
   */
  private async version(): Promise<LoadedWorld> {
    if (this.loaded !== null) {
      return this.loaded;
    }
    const progress = this.options.progress;
    progress({ type: "started", step: "world", total: 1 });
    const onFile = (file: WorldFile): void => {
      if (file === "pointer") {
        progress({ type: "finished", step: "world" });
        progress({ type: "started", step: "manifests", total: MANIFEST_FILES + (this.options.looks === undefined ? 0 : 1) });
      } else {
        progress({ type: "progressed", step: "manifests", done: file === "manifest" ? 1 : 2 });
      }
    };
    const running = this.options.running === undefined ? null : await this.options.running();
    gameReport.worldSource = "pointer";
    if (running !== null) {
      try {
        this.loaded = await loadWorldVersion(this.options.base, this.options.worldId, running.version, onFile);
        gameReport.worldSource = running.source;
        return this.loaded;
      } catch (error) {
        console.warn(`world ${running.version} named by ${running.source} did not load; reading the pointer`, error);
        progress({ type: "started", step: "world", total: 1 });
      }
    }
    this.loaded = await loadWorld(this.options.base, this.options.worldId, this.options.pointer, onFile);
    return this.loaded;
  }

  /**
   * The version's object types, from its contract, and its creature and NPC looks, fetched together: the manifests step's third
   * file.
   *
   * @param world The loaded world.
   * @returns The object types and where they came from.
   */
  private async objectTypes(world: LoadedWorld): Promise<GameObjectTypes> {
    if (this.types === null) {
      const [types, looks, npcLooks] = await Promise.all([loadGameObjectTypes(world), loadCreatureLooks(world), loadNpcLooks(world)]);
      this.looks = looks;
      this.npcLooks = npcLooks;
      this.types = types;
      this.options.progress({ type: "progressed", step: "manifests", done: 3 });
    }
    return this.types;
  }

  /**
   * The character manifest, and then the look sheets' pointer when the page draws composed looks (ADR-094 decision
   * 16): the manifests step's last files. A pointer that does not load fails the boot, since every player is drawn
   * from the looks and the page has no other figure (ADR-094 note 88, `LookFigures.start`); the looks are started
   * again by a retry, whether the pointer or a sheet of the fallback look failed, while the manifest itself is kept.
   *
   * @returns The manifest.
   * @throws Error naming the URL and its status when it does not arrive, or what is malformed.
   */
  private async characterManifest(): Promise<CharacterManifest> {
    if (this.characters === null) {
      const url = `${this.options.base}/${CHARACTER_MANIFEST}`;
      const response = await fetchNamed(url);
      if (!response.ok) {
        throw new Error(`${url} responded ${response.status}`);
      }
      const worn = await withOwnLook(parseCharacterManifest(await response.json()), this.options.ownLook, this.options.base, (line) => console.warn(line));
      this.ownLook = worn.look;
      this.characters = worn.manifest;
    }
    const looks = this.options.looks;
    if (looks !== undefined && !looks.started) {
      this.options.progress({ type: "progressed", step: "manifests", done: MANIFEST_FILES });
      await looks.start(this.scene, this.options.base, this.options.ownAppearance?.() ?? null);
    }
    this.options.progress({ type: "finished", step: "manifests" });
    return this.characters;
  }

  /**
   * The atlas pages of the map the page stands on, and the first time the character's strips, loaded together by
   * Phaser's loader, then the pages registered as atlas textures and their solid pixels readied, and the pages no map in
   * reach let go of (`AtlasPageHolder`, ADR-109 decisions 8 and 11). Only what the texture manager lacks is asked for, so a
   * retry fetches only what failed, and a change of map to one whose pages are held asks for nothing and shows no step.
   * The atlas step counts the pages it loads. The fallback look's piece sheets load beside the first batch and count in
   * the character step (ADR-094 decision 16); one that does not load fails the boot (`LookFigures.loadSheets`).
   *
   * @param world The loaded world.
   * @param characters The character manifest.
   * @param mapId The map the page stands on.
   * @param progress Where the steps are reported.
   * @throws Error naming the first file that failed, or one that arrived but could not be read, or saying that the
   *   fallback look's sheets did not load.
   */
  private async artwork(world: LoadedWorld, characters: CharacterManifest, mapId: string, progress: (event: LoadingEvent) => void): Promise<void> {
    const textures = this.scene.textures;
    const plan = await this.atlas.plan(world, mapId);
    const first = !this.art;
    const strips = first ? characterStrips(characters, this.options.base, EDITOR_SHEETS) : [];
    const looks = first ? this.options.looks : undefined;
    const lookSheets = looks?.bootSheets ?? 0;
    if (!first && plan.missing.length === 0) {
      this.atlas.finish(world, plan, mapId);
      return;
    }
    const report = (): void => {
      progress({ type: "progressed", step: "atlas", done: plan.missing.filter((page) => textures.exists(page.texture) || textures.exists(page.key)).length });
      if (first) {
        progress({ type: "progressed", step: "character", done: strips.filter((strip) => textures.exists(strip.key)).length + Math.min(lookSheets, looks?.bootSheetsLoaded ?? 0) });
      }
    };
    progress({ type: "started", step: "atlas", total: plan.missing.length });
    this.atlas.noteStep(plan.missing.length);
    if (first) {
      progress({ type: "started", step: "character", total: strips.length + lookSheets });
    }
    report();
    const missingStrips = strips.filter((strip) => !textures.exists(strip.key));
    const sheets = looks?.loadSheets().then(
      (): string | null => {
        report();
        return null;
      },
      (error: unknown): string => (error instanceof Error ? error.message : String(error)),
    );
    if (plan.missing.length + missingStrips.length > 0) {
      const failure = await loadFiles(this.scene, plan.missing, missingStrips, report);
      if (failure !== null) {
        throw new Error(failure);
      }
    }
    const sheetsFailure = (await sheets) ?? null;
    if (sheetsFailure !== null) {
      throw new Error(sheetsFailure);
    }
    this.atlas.finish(world, plan, mapId);
    progress({ type: "finished", step: "atlas" });
    if (first) {
      progress({ type: "finished", step: "character" });
    }
    this.art = true;
  }

  /**
   * Loads the atlas pages of a map the page is about to need, without the loading screen: a neighbour whose seam the view
   * is reaching (ADR-147 decision 8; P1.4-T191 calls it). Pages already held are not asked for again, and calls made
   * together for the same map share one load. Does nothing before the first boot has finished.
   *
   * @param mapId The map whose pages to load.
   * @throws Error naming the first file that failed, or one that arrived but could not be read.
   */
  public async preloadPages(mapId: string): Promise<void> {
    if (this.loaded !== null && this.art) {
      await this.atlas.preload(this.loaded, mapId);
    }
  }

  /**
   * Every chunk of the map the boot does not hold yet, fetched together; each one counts as it lands, and
   * the step fails only once every request has settled, so a retry asks again only for those that failed.
   *
   * @param world The loaded world.
   * @param map The map.
   * @param into Where the chunks go.
   * @param progress Where the step is reported.
   * @throws Error of the first chunk that failed.
   */
  private async fetchChunks(world: LoadedWorld, map: WorldMap, into: Map<string, CompiledChunk>, progress: (event: LoadingEvent) => void): Promise<void> {
    const chunkSize = world.manifest.chunkSize;
    const columns = Math.ceil(map.width / chunkSize);
    const rows = Math.ceil(map.height / chunkSize);
    progress({ type: "started", step: "chunks", total: columns * rows });
    progress({ type: "progressed", step: "chunks", done: into.size });
    const requests: Promise<void>[] = [];
    for (let cy = 0; cy < rows; cy += 1) {
      for (let cx = 0; cx < columns; cx += 1) {
        if (into.has(chunkKey(cx, cy))) {
          continue;
        }
        requests.push(
          this.chunkOf(world, map.id, cx, cy).then((chunk) => {
            if (chunk.collision === null) {
              throw new Error(`client chunk ${cx},${cy} carries no collision grid; republish the world (ADR-032)`);
            }
            into.set(chunkKey(cx, cy), chunk);
            progress({ type: "progressed", step: "chunks", done: into.size });
          }),
        );
      }
    }
    const settled = await Promise.allSettled(requests);
    const failed = settled.find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (failed !== undefined) {
      throw failed.reason;
    }
  }

  /**
   * The collision grid of the whole map from every chunk's cells, and a copy of those cells as compiled,
   * which the scene restamps things over.
   *
   * @param world The loaded world.
   * @param map The map.
   * @param chunks The map's chunks.
   * @returns The grid and the compiled cells.
   */
  private assemble(world: LoadedWorld, map: WorldMap, chunks: ReadonlyMap<string, CompiledChunk>): { grid: CollisionGrid; baseCells: Uint8Array } {
    const chunkSize = world.manifest.chunkSize;
    const cellSize = chunkSize * COMPILED_COLLISION_CELLS_PER_TILE;
    const gridWidth = map.width * COMPILED_COLLISION_CELLS_PER_TILE;
    const gridHeight = map.height * COMPILED_COLLISION_CELLS_PER_TILE;
    const cells = new Uint8Array(gridWidth * gridHeight);
    const columns = Math.ceil(map.width / chunkSize);
    const rows = Math.ceil(map.height / chunkSize);
    for (let cy = 0; cy < rows; cy += 1) {
      for (let cx = 0; cx < columns; cx += 1) {
        const collision = chunks.get(chunkKey(cx, cy))?.collision;
        if (collision === undefined || collision === null) {
          continue;
        }
        for (let y = 0; y < cellSize && cy * cellSize + y < gridHeight; y += 1) {
          for (let x = 0; x < cellSize && cx * cellSize + x < gridWidth; x += 1) {
            cells[(cy * cellSize + y) * gridWidth + cx * cellSize + x] = collision[y * cellSize + x] as number;
          }
        }
      }
    }
    return { grid: { width: gridWidth, height: gridHeight, cells, outside: SOLID }, baseCells: Uint8Array.from(cells) };
  }
}
